import React, { useState, useRef, useEffect, useCallback } from 'react';

interface ImageEditorProps {
    imageUrl: string;
    imageName: string;
    onSave: (editedImageBlob: Blob, newName: string) => void;
    onClose: () => void;
    theme?: 'light' | 'dark';
    language?: 'en' | 'sv';
}

const translations = {
    en: {
        title: 'Image Editor',
        crop: 'Crop',
        rotate: 'Rotate',
        flip: 'Flip',
        filters: 'Filters',
        adjust: 'Adjust',
        save: 'Save',
        cancel: 'Cancel',
        reset: 'Reset',
        rotateLeft: 'Rotate Left',
        rotateRight: 'Rotate Right',
        flipH: 'Flip Horizontal',
        flipV: 'Flip Vertical',
        brightness: 'Brightness',
        contrast: 'Contrast',
        saturation: 'Saturation',
        blur: 'Blur',
        original: 'Original',
        grayscale: 'Grayscale',
        sepia: 'Sepia',
        vintage: 'Vintage',
        cool: 'Cool',
        warm: 'Warm',
        vivid: 'Vivid',
        saving: 'Saving...',
        aspectRatio: 'Aspect Ratio',
        freeform: 'Freeform',
    },
    sv: {
        title: 'Bildredigerare',
        crop: 'Beskär',
        rotate: 'Rotera',
        flip: 'Vänd',
        filters: 'Filter',
        adjust: 'Justera',
        save: 'Spara',
        cancel: 'Avbryt',
        reset: 'Återställ',
        rotateLeft: 'Rotera vänster',
        rotateRight: 'Rotera höger',
        flipH: 'Vänd horisontellt',
        flipV: 'Vänd vertikalt',
        brightness: 'Ljusstyrka',
        contrast: 'Kontrast',
        saturation: 'Mättnad',
        blur: 'Oskärpa',
        original: 'Original',
        grayscale: 'Gråskala',
        sepia: 'Sepia',
        vintage: 'Vintage',
        cool: 'Kall',
        warm: 'Varm',
        vivid: 'Livlig',
        saving: 'Sparar...',
        aspectRatio: 'Bildförhållande',
        freeform: 'Fritt',
    }
};

const filterPresets: { [key: string]: string } = {
    original: 'none',
    grayscale: 'grayscale(100%)',
    sepia: 'sepia(100%)',
    vintage: 'sepia(40%) contrast(90%) brightness(95%)',
    cool: 'saturate(90%) hue-rotate(15deg)',
    warm: 'saturate(110%) sepia(20%)',
    vivid: 'saturate(150%) contrast(110%)',
};

const ImageEditor: React.FC<ImageEditorProps> = ({
    imageUrl,
    imageName,
    onSave,
    onClose,
    theme = 'dark',
    language = 'en'
}) => {
    const isDark = theme === 'dark';
    const t = translations[language] || translations.en;

    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);

    const [activeTab, setActiveTab] = useState<'filters' | 'adjust' | 'rotate'>('filters');
    const [rotation, setRotation] = useState(0);
    const [flipH, setFlipH] = useState(false);
    const [flipV, setFlipV] = useState(false);
    const [activeFilter, setActiveFilter] = useState('original');
    const [adjustments, setAdjustments] = useState({
        brightness: 100,
        contrast: 100,
        saturation: 100,
        blur: 0
    });
    const [isSaving, setIsSaving] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    // Load image
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            imageRef.current = img;
            setImageLoaded(true);
            renderCanvas();
        };
        img.src = imageUrl;
    }, [imageUrl]);

    // Build CSS filter string
    const getCSSFilter = useCallback(() => {
        const preset = filterPresets[activeFilter] || 'none';
        const adjustFilter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%) blur(${adjustments.blur}px)`;
        return preset === 'none' ? adjustFilter : `${preset} ${adjustFilter}`;
    }, [activeFilter, adjustments]);

    // Render to canvas
    const renderCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const img = imageRef.current;
        if (!canvas || !img) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        canvas.width = img.width;
        canvas.height = img.height;

        // Apply transformations
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        ctx.filter = getCSSFilter();
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        ctx.restore();
    }, [rotation, flipH, flipV, getCSSFilter]);

    useEffect(() => {
        if (imageLoaded) {
            renderCanvas();
        }
    }, [imageLoaded, renderCanvas]);

    // Reset all edits
    const handleReset = () => {
        setRotation(0);
        setFlipH(false);
        setFlipV(false);
        setActiveFilter('original');
        setAdjustments({ brightness: 100, contrast: 100, saturation: 100, blur: 0 });
    };

    // Save edited image
    const handleSave = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        setIsSaving(true);

        // Render final state
        renderCanvas();

        canvas.toBlob((blob) => {
            if (blob) {
                const newName = imageName.replace(/\.[^.]+$/, '_edited.jpg');
                onSave(blob, newName);
            }
            setIsSaving(false);
        }, 'image/jpeg', 0.92);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className={`w-full max-w-5xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col ${isDark ? 'bg-slate-900' : 'bg-white'}`}>
                {/* Header */}
                <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <span className="material-icons text-indigo-500">tune</span>
                        {t.title}
                    </h2>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleReset}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                        >
                            {t.reset}
                        </button>
                        <button
                            onClick={onClose}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'}`}
                        >
                            {t.cancel}
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                        >
                            <span className="material-icons text-sm">{isSaving ? 'sync' : 'save'}</span>
                            {isSaving ? t.saving : t.save}
                        </button>
                    </div>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Canvas Preview */}
                    <div className="flex-1 flex items-center justify-center p-6 overflow-auto bg-black/20">
                        <canvas
                            ref={canvasRef}
                            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                            style={{ filter: getCSSFilter() }}
                        />
                    </div>

                    {/* Tools Sidebar */}
                    <div className={`w-72 border-l flex flex-col ${isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-gray-50'}`}>
                        {/* Tabs */}
                        <div className="flex border-b border-slate-700">
                            {(['filters', 'adjust', 'rotate'] as const).map(tab => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === tab
                                        ? 'bg-indigo-600 text-white'
                                        : isDark ? 'text-slate-400 hover:bg-slate-700' : 'text-gray-500 hover:bg-gray-100'
                                        }`}
                                >
                                    {tab === 'filters' ? t.filters : tab === 'adjust' ? t.adjust : t.rotate}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto p-4">
                            {activeTab === 'filters' && (
                                <div className="grid grid-cols-2 gap-3">
                                    {Object.keys(filterPresets).map(filter => (
                                        <button
                                            key={filter}
                                            onClick={() => setActiveFilter(filter)}
                                            className={`p-3 rounded-xl text-center text-sm font-medium transition-all ${activeFilter === filter
                                                ? 'bg-indigo-600 text-white ring-2 ring-indigo-400'
                                                : isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white hover:bg-gray-100 shadow'
                                                }`}
                                        >
                                            {(t as any)[filter] || filter}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {activeTab === 'adjust' && (
                                <div className="space-y-6">
                                    {[
                                        { key: 'brightness', label: t.brightness, min: 0, max: 200 },
                                        { key: 'contrast', label: t.contrast, min: 0, max: 200 },
                                        { key: 'saturation', label: t.saturation, min: 0, max: 200 },
                                        { key: 'blur', label: t.blur, min: 0, max: 10 }
                                    ].map(({ key, label, min, max }) => (
                                        <div key={key}>
                                            <div className="flex justify-between text-sm mb-2">
                                                <span>{label}</span>
                                                <span className="text-indigo-400">{(adjustments as any)[key]}{key === 'blur' ? 'px' : '%'}</span>
                                            </div>
                                            <input
                                                type="range"
                                                min={min}
                                                max={max}
                                                value={(adjustments as any)[key]}
                                                onChange={(e) => setAdjustments(prev => ({ ...prev, [key]: Number(e.target.value) }))}
                                                className="w-full accent-indigo-500"
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {activeTab === 'rotate' && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        <button
                                            onClick={() => setRotation(r => r - 90)}
                                            className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white hover:bg-gray-100 shadow'}`}
                                        >
                                            <span className="material-icons text-2xl">rotate_left</span>
                                            <span className="text-xs">{t.rotateLeft}</span>
                                        </button>
                                        <button
                                            onClick={() => setRotation(r => r + 90)}
                                            className={`p-4 rounded-xl flex flex-col items-center gap-2 ${isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white hover:bg-gray-100 shadow'}`}
                                        >
                                            <span className="material-icons text-2xl">rotate_right</span>
                                            <span className="text-xs">{t.rotateRight}</span>
                                        </button>
                                        <button
                                            onClick={() => setFlipH(f => !f)}
                                            className={`p-4 rounded-xl flex flex-col items-center gap-2 ${flipH ? 'bg-indigo-600 text-white' : isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white hover:bg-gray-100 shadow'}`}
                                        >
                                            <span className="material-icons text-2xl">flip</span>
                                            <span className="text-xs">{t.flipH}</span>
                                        </button>
                                        <button
                                            onClick={() => setFlipV(f => !f)}
                                            className={`p-4 rounded-xl flex flex-col items-center gap-2 ${flipV ? 'bg-indigo-600 text-white' : isDark ? 'bg-slate-700 hover:bg-slate-600' : 'bg-white hover:bg-gray-100 shadow'}`}
                                        >
                                            <span className="material-icons text-2xl" style={{ transform: 'rotate(90deg)' }}>flip</span>
                                            <span className="text-xs">{t.flipV}</span>
                                        </button>
                                    </div>
                                    <div className="text-center">
                                        <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                            Rotation: {rotation}°
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageEditor;
